import { createContext, useContext } from "react";
import { useLocation } from "react-router-dom";

const LangCtx = createContext({ lang: "en", prefix: "", altPath: "/fr", basePath: "/" });

export function LangProvider({ children }) {
  const { pathname } = useLocation();
  const lang = pathname === "/fr" || pathname.startsWith("/fr/") ? "fr" : "en";
  const basePath = lang === "fr" ? pathname.slice(3) || "/" : pathname;
  const prefix = lang === "fr" ? "/fr" : "";
  const altPath = lang === "fr" ? basePath : "/fr" + (pathname === "/" ? "" : pathname);
  return (
    <LangCtx.Provider value={{ lang, prefix, altPath, basePath }}>{children}</LangCtx.Provider>
  );
}

export function useLang() {
  return useContext(LangCtx);
}

export function fmt(tpl, vars) {
  return tpl.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : `{${k}}`));
}

export const UI = {
  en: {
    nav_routes: "Routes",
    nav_blog: "Blog",
    nav_resources: "Resources",
    nav_faq: "FAQ",
    nav_contact: "Contact",
    nav_quote: "Request a Quote",
    enq_title: "Request a quote",
    enq_sub: "Tell us about your journey and we reply with your fixed quote as soon as possible.",
    enq_name: "Your name",
    enq_email: "Email address",
    enq_phone: "Phone / WhatsApp (optional)",
    enq_from: "Pickup address or city",
    enq_to: "Drop-off address or city",
    enq_pets: "Pets (e.g. 1 dog, 1 cat)",
    enq_date: "Travel date (approximate is fine)",
    enq_message: "Anything else we should know?",
    enq_send: "Send enquiry",
    enq_sending: "Sending...",
    enq_ok_h: "Enquiry sent",
    enq_ok_p: "Thank you - we have received your enquiry and will reply with your fixed quote shortly. In a hurry? Message us on WhatsApp.",
    enq_err: "Something went wrong - please try again or message us on WhatsApp.",
    enq_passengers: "Passengers",
    enq_luggage: "Luggage",
    enq_lug_light: "Light - 1-2 bags",
    enq_lug_medium: "Medium - 3-4 bags",
    enq_lug_lots: "Lots - 5+ bags or boxes",
    enq_vehicle_h: "Choose your vehicle",
    enq_v1_name: "Standard - VW Passat",
    enq_v1_sub: "Comfortable saloon - up to 3 passengers",
    enq_v2_name: "Executive - Mercedes E-Class",
    enq_v2_sub: "Extra comfort - up to 3 passengers",
    enq_v3_name: "Minibus - VW Transporter LWB",
    enq_v3_sub: "Up to 8 passengers and lots of luggage",
    enq_next_h2: "What happens after you send it",
    enq_next_p1: "We reply with your exact fixed quote as soon as possible. The fare is mileage-based and includes the vehicle, your dedicated driver, motorway tolls and as many comfort stops as your pet needs - the only extra is the Eurotunnel ticket, paid at the terminal on the day.",
    enq_next_p2: "We also send you a personalised document timeline for your travel date: microchip, rabies vaccination, Animal Health Certificate or EU pet passport, and the tapeworm window if your dog is entering the UK. Prefer to talk first? Message us on WhatsApp any time - the green live chat button is on every page.",
    bl_intro_p1: "Every guide here is written from real crossings between London and Europe - the documents that actually get checked at the tunnel, the meeting points that work at Euston, St Pancras and Paris CDG, and the timings that keep pets calm.",
    bl_intro_p2: "Start with your route pair: each guide covers the journey in both directions, what it costs, which documents you need for each direction, and the mistakes that most often delay a crossing.",
    ri_hub_h2: "Two meeting points: London and Paris CDG",
    ri_hub_p1: "London is the UK's natural meeting point. Most of our passengers arrive by train from across Britain, so we collect directly from the concourse at Euston, St Pancras International, King's Cross, Victoria and Paddington - as well as from homes, hotels and both London airports, with live flight tracking at Heathrow and Gatwick.",
    ri_hub_p2: "On the European side, Paris Charles de Gaulle plays the same role: the continent's great hub. We meet you at the official terminal meeting point at CDG (Terminals 1, 2E, 2F or 3), track your flight, and drive you and your pet straight through the tunnel to your UK address - or the reverse, from any London door to the CDG departures hall.",
    hero_eyebrow: "London ↔ Europe · DEFRA Type 2 Authorised",
    hero_l1: "Your pet beside you,",
    hero_l2: "Paris to London,",
    hero_l3: "door to door.",
    hero_sub: "A personal pet chauffeur service across the Channel. You and your dog or cat travel together in a private, climate-controlled MPV via Eurotunnel Le Shuttle - no cargo holds, no strangers, no separation.",
    hero_cta1: "Get an Instant Quote",
    hero_cta2: "Explore Routes",
    badge1: "5-star rated pet chauffeur",
    badge2: "DEFRA Type 2 authorised",
    badge3: "Daily Eurotunnel crossings",
    pill_label: "Mileage-based fare",
    pill_suffix: "/mile · fixed quote",
    services_eyebrow: "The service",
    services_h2: "A chauffeur service, not a shipping company",
    card1_t: "You travel together",
    card1_b: "Your pet rides beside you in a private MPV for the whole journey - through check-in, onto Le Shuttle, and all the way to your new front door. One vehicle, one dedicated driver, zero handovers.",
    card2_t: "DEFRA Type 2",
    card2_b: "Every journey is operated by drivers holding their own DEFRA Type 2 animal transporter authorisation, commercial insurance and private hire licensing.",
    card3_t: "Airport meet & greet",
    card3_b: "Landing at CDG or Orly? Your chauffeur tracks the flight, waits at arrivals with a name board and helps with luggage and carriers.",
    card4_t: "Paperwork checked",
    card4_b: "Microchip, rabies, AHC or EU passport, tapeworm timing for dogs - we verify every document before departure so nothing fails at the terminal.",
    card5_t: "All tolls included",
    card5_b: "Fixed quotes cover every motorway toll. The Eurotunnel ticket is shown separately and paid directly at the terminal.",
    city_paris_sub: "CDG · Orly · every arrondissement",
    city_london: "London",
    city_london_sub: "All boroughs · Heathrow · Gatwick",
    routes_eyebrow: "Popular routes",
    routes_h2: "Cross-channel journeys, priced by the mile",
    routes_all: "All routes",
    route_card_cta: "Route guide",
    steps_eyebrow: "How it works",
    steps_h2: "Four steps to the other side of the Channel",
    step1_t: "Get your fixed quote",
    step1_d: "Choose pickup and drop-off; the fare calculator prices the exact mileage instantly.",
    step2_t: "We verify documents",
    step2_d: "Microchip, rabies, AHC or EU passport, tapeworm timing - checked before travel day.",
    step3_t: "Chauffeur at your door",
    step3_d: "Your driver collects you and your pet, at home or at the airport arrivals hall.",
    step4_t: "Cross together",
    step4_d: "Pet check-in at the tunnel, 35 minutes on Le Shuttle, and onward to your new door.",
    faq_eyebrow: "FAQ",
    faq_h2: "Common questions about Paris - London pet travel",
    cta_h2: "Ready when your pet is",
    cta_sub: "Get a fixed, mileage-based quote in under a minute - or message us on WhatsApp and we will plan the journey together.",
    cta_btn1: "Get an Instant Quote",
    cta_btn2: "WhatsApp Us",
    footer_blurb: "Door-to-door pet taxi between London and Europe. You and your pet travel together via Eurotunnel Le Shuttle.",
    footer_routes: "Routes",
    footer_explore: "Explore",
    footer_contact: "Contact",
    footer_book: "Request a Quote",
    footer_blog: "Blog",
    footer_bottom_routes: "London ↔ Paris · London ↔ Brussels · London ↔ Amsterdam",
    book_eyebrow: "Instant quote & booking",
    book_h1: "Plan your crossing",
    step_journey: "Journey",
    step_pets: "Pets",
    step_details: "Your details",
    pickup: "Pickup",
    dropoff: "Drop-off",
    choose_pickup: "Choose pickup location",
    choose_dropoff: "Choose drop-off location",
    dogs: "Dogs",
    cats: "Cats",
    travel_date: "Preferred travel date",
    pets_about: "About your pets",
    pets_about_hint: "Names, breeds, approximate weight, carrier needs",
    pets_placeholder: "e.g. Milo, golden retriever, 28 kg, travels on the back seat with a harness. Luna, tabby cat, 4 kg, in her carrier.",
    docs_notice: "Please have ready for travel day: microchip, valid rabies vaccination, Animal Health Certificate or EU pet passport, and - for dogs entering the UK - a tapeworm treatment recorded 24-120 hours before arrival.",
    full_name: "Full name",
    phone: "Phone / WhatsApp",
    email: "Email",
    notes: "Notes (optional)",
    notes_ph: "Flight number, extra luggage, accessibility needs...",
    back: "Back",
    continue_btn: "Continue",
    request_booking: "Request Booking",
    fare_estimate: "Fare estimate",
    journey_min: "Journey fare (minimum)",
    additional_pets: "Additional pets",
    fixed_quote: "Fixed quote",
    mileage_tpl: "{miles} miles × £{rate}",
    tunnel_label: "Eurotunnel ticket",
    tunnel_note: "paid separately at the terminal",
    tunnel_pets: "+ £22-45 per pet, per direction",
    cancel_note: "Cancellation: more than 7 days before travel we retain a £50 admin fee; within 7 days, £150. Waiting time for client delays is charged at £100.",
    from_tpl: "from £{price}",
    fixed_fare: "Route fare (fixed)",
    incl_note: "Motorway tolls are included in the mileage rate. The Eurotunnel ticket is not included and is paid directly at the terminal.",
    calculating: "Calculating mileage...",
    fare_empty: "Choose a pickup and drop-off to see your fixed, mileage-based quote.",
    min_line: "£{r}/mile · £{m} minimum · Eurotunnel separate",
    success_h1: "Booking request received",
    success_pre: "Your reference is",
    success_post: "A confirmation email is on its way, and our chauffeur team will confirm your journey shortly.",
    success_fare: "Estimated fixed fare",
    back_home: "Back to Home",
    ri_eyebrow: "Routes",
    ri_h1: "Every crossing, door to door",
    ri_sub: "Our pet chauffeur routes across the Channel. Each route page covers timings, the Eurotunnel pet check-in, document checklists and a direct link to your instant fixed quote.",
    popular: "Popular",
    door_to_door: "door to door",
    approx: "approx.",
    rp_eyebrow: "Pet transport route",
    rp_together: "you travel together",
    rp_cta: "Get a fixed quote for this route",
    cl_h2: "Document checklist for this route",
    rp_faq_h2: "Questions about",
    rp_bottom_tpl: "{from} to {to}, fixed in advance",
    rp_bottom_sub: "Mileage-based quote in under a minute - tolls included, Eurotunnel ticket paid separately at the terminal.",
    rp_expect_h2: "What to expect on this journey",
    rp_tip_label: "Good to know on this route",
    rp_expect_p1_tpl: "Your chauffeur collects you and your pet at your address in {from} at the time that suits you. From there it is a calm, direct drive of around {miles} miles - roughly {duration} door to door - in a private, climate-controlled vehicle. Your dog or cat stays beside you the whole way: no cargo hold, no handovers and no other animals on board, with comfort stops whenever your pet needs one.",
    rp_expect_p2_tpl: "At the Eurotunnel terminal we handle the pet check-in together - the microchip scan and document check usually take about 15 minutes - then your pet stays with you in the car for the 35-minute crossing. On the other side we drive straight to your final address in {to}, whether that is a hotel, a new home or a family doorstep.",
    rp_expect_p3_tpl: "The fare for {from} to {to} is fixed before you book and starts from £{price}. It covers the vehicle, your dedicated driver, tolls and as many comfort stops as your pet needs. The Eurotunnel ticket is paid separately at the terminal, and we never add weekend or holiday surcharges.",
    ri_how_h2: "How every route works",
    ri_how_p1: "Every route we run is private and door to door: one vehicle, one dedicated driver, and your pet travelling beside you for the whole journey. All of our drivers hold DEFRA Type 2 animal transporter authorisation, and every crossing uses Eurotunnel Le Shuttle, where pets stay in the car with you.",
    ri_how_p2: "Prices are mileage-based and fixed before you book, so the quote you see is the price you pay. Each route page shows the distance, the typical door-to-door time and the starting fare, and tolls are always included. The Eurotunnel ticket is the only extra, paid at the terminal on the day.",
    ri_how_p3: "For documents, the rules are the same on every route: your pet needs a microchip, a valid rabies vaccination and an Animal Health Certificate or EU pet passport, and dogs entering the UK also need a tapeworm treatment given by a vet 24 to 120 hours before arrival. Each route page includes a full checklist and answers to the questions owners ask most about that journey.",
    ri_hub_h2: "Two meeting points: London and Paris CDG",
    ri_hub_p1: "London is where most of our journeys begin or end, because it is where Britain's trains converge. If you are travelling from Manchester, Edinburgh or anywhere else in the UK, simply arrive at Euston, St Pancras International, King's Cross or Paddington and your chauffeur meets you on the concourse - no taxi changes, no dragging a pet carrier across the city. Heathrow and Gatwick pickups include live flight tracking, so a delayed flight never breaks the connection.",
    ri_hub_p2: "On the European side, Paris Charles de Gaulle plays the same role. We meet you at the official terminal meeting point with a name board - Terminals 2E and 2F just past customs, or the correct arrivals hall for Terminals 1 and 3 - track your flight, and handle the carrier and luggage while you stretch after a long-haul flight. From CDG we drive straight to Calais, through the tunnel, and on to your London door.",
    book_route: "Book this route",
    not_found_route: "Route not found",
    browse_routes: "Browse all routes",
    loading_route: "Loading route...",
    bl_eyebrow: "Travel journal",
    bl_h1: "Notes from the road between two capitals",
    read_guide: "Read guide",
    in_guide: "In this guide",
    min_read: "min read",
    quick_answers: "Quick answers",
    bp_cta_h2: "Travelling this route with your pet?",
    bp_cta_sub: "Fixed mileage-based quote in under a minute - you and your pet, door to door.",
    not_found_post: "Article not found",
    back_journal: "Back to the journal",
    loading_post: "Loading article...",
  },
  fr: {
    nav_routes: "Itinéraires",
    nav_blog: "Blog",
    nav_resources: "Ressources",
    nav_faq: "FAQ",
    nav_contact: "Contact",
    nav_quote: "Demander un devis",
    enq_title: "Demander un devis",
    enq_sub: "Parlez-nous de votre trajet et nous répondons avec votre devis fixe dès que possible.",
    enq_name: "Votre nom",
    enq_email: "Adresse email",
    enq_phone: "Téléphone / WhatsApp (optionnel)",
    enq_from: "Adresse ou ville de départ",
    enq_to: "Adresse ou ville d'arrivée",
    enq_pets: "Animaux (ex. 1 chien, 1 chat)",
    enq_date: "Date du voyage (approximative acceptée)",
    enq_message: "Autre chose à nous signaler ?",
    enq_send: "Envoyer la demande",
    enq_sending: "Envoi...",
    enq_ok_h: "Demande envoyée",
    enq_ok_p: "Merci - nous avons bien reçu votre demande et nous répondrons dès que possible avec votre devis fixe. Pressé ? Écrivez-nous sur WhatsApp.",
    enq_err: "Une erreur est survenue - réessayez ou écrivez-nous sur WhatsApp.",
    enq_passengers: "Passagers",
    enq_luggage: "Bagages",
    enq_lug_light: "Légers - 1-2 sacs",
    enq_lug_medium: "Moyens - 3-4 sacs",
    enq_lug_lots: "Beaucoup - 5+ sacs ou cartons",
    enq_vehicle_h: "Choisissez votre véhicule",
    enq_v1_name: "Standard - VW Passat",
    enq_v1_sub: "Berline confortable - jusqu'à 3 passagers",
    enq_v2_name: "Executive - Mercedes Classe E",
    enq_v2_sub: "Confort supérieur - jusqu'à 3 passagers",
    enq_v3_name: "Minibus - VW Transporter LWB",
    enq_v3_sub: "Jusqu'à 8 passagers et beaucoup de bagages",
    enq_next_h2: "Et après l'envoi ?",
    enq_next_p1: "Nous répondons avec votre devis fixe exact dès que possible. Le tarif est calculé au kilométrage et comprend le véhicule, votre chauffeur dédié, les péages et autant de pauses confort que nécessaire - le seul supplément est le billet Eurotunnel, réglé au terminal le jour même.",
    enq_next_p2: "Nous vous envoyons aussi un calendrier de documents personnalisé pour votre date : puce, vaccin antirabique, certificat sanitaire ou passeport européen, et la fenêtre du vermifuge si votre chien entre au Royaume-Uni. Vous préférez en parler d'abord ? Écrivez-nous sur WhatsApp à tout moment - le bouton vert de chat en direct est sur chaque page.",
    hero_eyebrow: "Londres ↔ Europe · Agréé DEFRA Type 2",
    hero_l1: "Votre animal à vos côtés,",
    hero_l2: "de Paris à Londres,",
    hero_l3: "porte à porte.",
    hero_sub: "Un service de chauffeur privé pour animaux à travers la Manche. Vous et votre chien ou chat voyagez ensemble dans un monospace privé climatisé via Eurotunnel Le Shuttle - sans soute, sans inconnus, sans séparation.",
    hero_cta1: "Obtenir un devis immédiat",
    hero_cta2: "Découvrir les itinéraires",
    badge1: "Chauffeur animalier 5 étoiles",
    badge2: "Agrément DEFRA Type 2",
    badge3: "Traversées Eurotunnel quotidiennes",
    pill_label: "Tarif au kilométrage",
    pill_suffix: "/mile · devis fixe",
    services_eyebrow: "Le service",
    services_h2: "Un service de chauffeur, pas une société de transport",
    card1_t: "Vous voyagez ensemble",
    card1_b: "Votre animal voyage à vos côtés dans un monospace privé pendant tout le trajet - de l'enregistrement à la navette Le Shuttle, jusqu'à votre nouvelle porte. Un seul véhicule, un chauffeur dédié, zéro correspondance.",
    card2_t: "DEFRA Type 2",
    card2_b: "Chaque trajet est assuré par des chauffeurs détenant leur propre agrément de transporteur d'animaux DEFRA Type 2, une assurance commerciale et une licence de transport privé.",
    card3_t: "Accueil aux aéroports",
    card3_b: "Vous atterrissez à CDG ou Orly ? Votre chauffeur suit le vol, vous attend aux arrivées avec une pancarte et aide avec bagages et caisses.",
    card4_t: "Documents vérifiés",
    card4_b: "Puce, rage, AHC ou passeport européen, timing du vermifuge pour les chiens - nous vérifions chaque document avant le départ pour que rien n'échoue au terminal.",
    card5_t: "Péages inclus",
    card5_b: "Les devis fixes couvrent tous les péages autoroutiers. Le billet Eurotunnel est indiqué séparément et réglé directement au terminal.",
    city_paris_sub: "CDG · Orly · tous les arrondissements",
    city_london: "Londres",
    city_london_sub: "Tous les quartiers · Heathrow · Gatwick",
    routes_eyebrow: "Itinéraires populaires",
    routes_h2: "Des traversées transmanche, tarifées au kilométrage",
    routes_all: "Tous les itinéraires",
    route_card_cta: "Guide de l'itinéraire",
    steps_eyebrow: "Comment ça marche",
    steps_h2: "Quatre étapes vers l'autre rive de la Manche",
    step1_t: "Obtenez votre devis fixe",
    step1_d: "Choisissez prise en charge et destination ; le calculateur chiffre le kilométrage exact instantanément.",
    step2_t: "Nous vérifions les documents",
    step2_d: "Puce, rage, AHC ou passeport européen, vermifuge - contrôlés avant le jour du départ.",
    step3_t: "Le chauffeur à votre porte",
    step3_d: "Votre chauffeur vous récupère, vous et votre animal, à domicile ou au hall des arrivées.",
    step4_t: "Traversez ensemble",
    step4_d: "Enregistrement animaux au tunnel, 35 minutes en Le Shuttle, puis route jusqu'à votre nouvelle porte.",
    faq_eyebrow: "FAQ",
    faq_h2: "Questions fréquentes sur le voyage Paris - Londres avec un animal",
    cta_h2: "Prêts quand votre animal l'est",
    cta_sub: "Obtenez un devis fixe au kilométrage en moins d'une minute - ou écrivez-nous sur WhatsApp et nous préparerons le voyage ensemble.",
    cta_btn1: "Obtenir un devis immédiat",
    cta_btn2: "WhatsApp",
    footer_blurb: "Taxi animalier porte à porte entre Londres et l'Europe. Vous et votre animal voyagez ensemble via Eurotunnel Le Shuttle.",
    footer_routes: "Itinéraires",
    footer_explore: "Explorer",
    footer_contact: "Contact",
    footer_book: "Demander un devis",
    footer_blog: "Blog",
    footer_bottom_routes: "Londres ↔ Paris · Londres ↔ Bruxelles · Londres ↔ Amsterdam",
    book_eyebrow: "Devis immédiat & réservation",
    book_h1: "Planifiez votre traversée",
    step_journey: "Trajet",
    step_pets: "Animaux",
    step_details: "Vos coordonnées",
    pickup: "Prise en charge",
    dropoff: "Destination",
    choose_pickup: "Choisissez le lieu de prise en charge",
    choose_dropoff: "Choisissez la destination",
    dogs: "Chiens",
    cats: "Chats",
    travel_date: "Date de voyage souhaitée",
    pets_about: "À propos de vos animaux",
    pets_about_hint: "Noms, races, poids approximatif, besoins de caisse",
    pets_placeholder: "ex. Milo, golden retriever, 28 kg, voyage sur la banquette avec un harnais. Luna, chatte tigrée, 4 kg, dans sa caisse.",
    docs_notice: "À préparer pour le jour du départ : puce électronique, vaccin antirabique valide, certificat sanitaire ou passeport européen, et - pour les chiens entrant au Royaume-Uni - un traitement vermifuge enregistré 24 à 120 h avant l'arrivée.",
    full_name: "Nom complet",
    phone: "Téléphone / WhatsApp",
    email: "E-mail",
    notes: "Notes (facultatif)",
    notes_ph: "Numéro de vol, bagages supplémentaires, besoins d'accessibilité...",
    back: "Retour",
    continue_btn: "Continuer",
    request_booking: "Demander la réservation",
    fare_estimate: "Estimation du tarif",
    journey_min: "Tarif trajet (minimum)",
    additional_pets: "Animaux supplémentaires",
    fixed_quote: "Devis fixe",
    mileage_tpl: "{miles} miles × £{rate}",
    tunnel_label: "Billet Eurotunnel",
    tunnel_note: "réglé séparément au terminal",
    tunnel_pets: "+ 22 à 45 £ par animal et par sens",
    cancel_note: "Annulation : plus de 7 jours avant le départ, 50 £ de frais ; moins de 7 jours, 150 £. Temps d'attente en cas de retard client : 100 £.",
    from_tpl: "à partir de £{price}",
    fixed_fare: "Tarif fixe d'itinéraire",
    incl_note: "Les péages autoroutiers sont inclus dans le tarif kilométrique. Le billet Eurotunnel n'est pas inclus et se règle directement au terminal.",
    calculating: "Calcul du kilométrage...",
    fare_empty: "Choisissez une prise en charge et une destination pour voir votre devis fixe au kilométrage.",
    min_line: "£{r}/mile · minimum £{m} · Eurotunnel séparé",
    success_h1: "Demande de réservation reçue",
    success_pre: "Votre référence est",
    success_post: "Un e-mail de confirmation est en route, et notre équipe de chauffeurs confirmera votre voyage sous peu.",
    success_fare: "Tarif fixe estimé",
    back_home: "Retour à l'accueil",
    ri_eyebrow: "Itinéraires",
    ri_h1: "Chaque traversée, porte à porte",
    ri_sub: "Nos itinéraires de chauffeur animalier à travers la Manche. Chaque page détaille horaires, enregistrement animaux Eurotunnel, checklist des documents et un lien direct vers votre devis fixe immédiat.",
    popular: "Populaire",
    door_to_door: "porte à porte",
    approx: "env.",
    rp_eyebrow: "Itinéraire animalier",
    rp_together: "vous voyagez ensemble",
    rp_cta: "Devis fixe pour cet itinéraire",
    cl_h2: "Checklist des documents pour cet itinéraire",
    rp_faq_h2: "Questions fréquentes sur",
    rp_bottom_tpl: "De {from} à {to}, prix fixé à l'avance",
    rp_bottom_sub: "Devis au kilométrage en moins d'une minute - péages inclus, billet Eurotunnel réglé séparément au terminal.",
    rp_expect_h2: "Comment se déroule ce trajet",
    rp_tip_label: "Bon à savoir sur cet itinéraire",
    rp_expect_p1_tpl: "Votre chauffeur vous récupère, vous et votre animal, à votre adresse à {from}, à l'heure qui vous convient. Ensuite, c'est un trajet direct et tranquille d'environ {miles} miles - soit environ {duration} porte à porte - dans un véhicule privé climatisé. Votre chien ou chat reste à vos côtés tout du long : pas de soute, pas de transfert, aucun autre animal à bord, avec des pauses confort dès que votre animal en a besoin.",
    rp_expect_p2_tpl: "Au terminal Eurotunnel, nous gérons ensemble l'enregistrement des animaux - la lecture de la puce et la vérification des documents prennent environ 15 minutes - puis votre animal reste avec vous dans la voiture pendant la traversée de 35 minutes. De l'autre côté, nous vous conduisons directement à votre adresse finale à {to}, que ce soit un hôtel, un nouveau logement ou le pas de porte de votre famille.",
    rp_expect_p3_tpl: "Le tarif de {from} à {to} est fixé avant la réservation et commence à partir de £{price}. Il comprend le véhicule, votre chauffeur dédié, les péages et autant de pauses que nécessaire. Le billet Eurotunnel est réglé séparément au terminal, et nous n'appliquons aucun supplément week-end ou jour férié.",
    ri_how_h2: "Comment fonctionne chaque itinéraire",
    ri_how_p1: "Chaque trajet est privé et porte à porte : un véhicule, un chauffeur dédié, et votre animal qui voyage à vos côtés pendant tout le trajet. Tous nos chauffeurs sont titulaires de l'agrément DEFRA Type 2 pour le transport d'animaux, et chaque traversée passe par Eurotunnel Le Shuttle, où les animaux restent dans la voiture avec vous.",
    ri_how_p2: "Les prix sont calculés au kilométrage et fixés avant la réservation : le devis affiché est le prix payé. Chaque page d'itinéraire indique la distance, la durée typique porte à porte et le tarif de départ, et les péages sont toujours inclus. Le billet Eurotunnel est le seul supplément, réglé au terminal le jour même.",
    ri_how_p3: "Pour les documents, les règles sont identiques sur chaque itinéraire : votre animal doit avoir une puce électronique, une vaccination antirabique valide et un certificat sanitaire (AHC) ou un passeport européen ; les chiens entrant au Royaume-Uni doivent aussi recevoir un traitement antiparasitaire administré par un vétérinaire 24 à 120 heures avant l'arrivée. Chaque page inclut la checklist complète et les réponses aux questions les plus fréquentes pour ce trajet.",
    ri_hub_h2: "Deux points de rencontre : Londres et Paris CDG",
    ri_hub_p1: "Londres est le point de départ ou d'arrivée de la plupart de nos trajets, parce que c'est là que convergent les trains britanniques. Si vous venez de Manchester, d'Édimbourg ou d'ailleurs, arrivez simplement à Euston, St Pancras International, King's Cross ou Paddington et votre chauffeur vous attend sur le parvis - pas de changement de taxi, pas de caisse de transport traînée à travers la ville. Les prises en charge à Heathrow et Gatwick incluent le suivi de vol : un retard ne casse jamais la correspondance.",
    ri_hub_p2: "Côté européen, Paris Charles de Gaulle joue le même rôle. Nous vous attendons au point de rendez-vous officiel du terminal avec une pancarte - terminaux 2E et 2F juste après la douane, ou le bon hall d'arrivée pour les terminaux 1 et 3 - nous suivons votre vol, et nous gérons la caisse et les bagages pendant que vous vous dégourdissez les jambes après un long-courrier. De CDG, nous filons directement vers Calais, le tunnel, puis votre porte à Londres.",
    book_route: "Réserver cet itinéraire",
    not_found_route: "Itinéraire introuvable",
    browse_routes: "Voir tous les itinéraires",
    loading_route: "Chargement de l'itinéraire...",
    bl_eyebrow: "Journal de voyage",
    bl_h1: "Carnets de route entre deux capitales",
    read_guide: "Lire le guide",
    in_guide: "Dans ce guide",
    min_read: "min de lecture",
    quick_answers: "Réponses rapides",
    bp_cta_h2: "Vous voyagez sur cet itinéraire avec votre animal ?",
    bp_cta_sub: "Devis fixe au kilométrage en moins d'une minute - vous et votre animal, porte à porte.",
    not_found_post: "Article introuvable",
    back_journal: "Retour au journal",
    loading_post: "Chargement de l'article...",
  },
};

export const FAQS = {
  en: [
    { q: "Do pets travel in the car with their owner?", a: "Yes. This is a private chauffeur service where you and your pet travel together in the same vehicle for the entire journey, including the Eurotunnel Le Shuttle crossing. Your pet is never separated from you and never travels in a cargo hold." },
    { q: "How much does pet transport from Paris to London cost?", a: "Every journey is priced by mileage with a fixed quote before you book. Tap Get instant quote and you will have an exact figure in under a minute, based on your pickup point, destination and number of pets." },
    { q: "Can I travel from London to Paris with my pet as well?", a: "Yes. The service runs daily in both directions between Paris and London, as well as from Brussels and Amsterdam to London." },
    { q: "Are dogs allowed on the Eurostar?", a: "No - Eurostar passenger trains do not accept pets at all. That is exactly why we drive: your dog or cat travels with you in our vehicle through Eurotunnel Le Shuttle, staying beside you the whole way, door to door." },
    { q: "What documents does my pet need to travel between France and the UK?", a: "Your pet needs a microchip, a valid rabies vaccination, and an Animal Health Certificate or EU pet passport. Dogs also need a tapeworm treatment administered by a vet 24 to 120 hours before arriving in the UK." },
    { q: "Where do you collect from in Paris?", a: "Anywhere - Charles de Gaulle and Orly airports, every Paris arrondissement, Disneyland Paris and the suburbs. Your chauffeur meets you at arrivals with a name board for airport pickups." },
  ],
  fr: [
    { q: "Les animaux voyagent-ils dans la voiture avec leur propriétaire ?", a: "Oui. C'est un service de chauffeur privé : vous et votre animal voyagez ensemble dans le même véhicule pendant tout le trajet, y compris la traversée Eurotunnel Le Shuttle. Votre animal n'est jamais séparé de vous et ne voyage jamais en soute." },
    { q: "Combien coûte un transport d'animaux de Paris à Londres ?", a: "Chaque trajet est tarifé au kilométrage avec un devis fixe avant réservation. Touchez « Devis immédiat » et obtenez un montant exact en moins d'une minute, selon votre point de prise en charge, votre destination et le nombre d'animaux." },
    { q: "Puis-je aussi voyager de Londres à Paris avec mon animal ?", a: "Oui. Le service fonctionne quotidiennement dans les deux sens entre Paris et Londres, ainsi qu'entre Bruxelles ou Amsterdam et Londres." },
    { q: "Les chiens sont-ils autorisés dans l'Eurostar ?", a: "Non - l'Eurostar n'accepte aucun animal à bord. C'est exactement pour cela que nous conduisons : votre chien ou chat voyage avec vous dans notre véhicule via Eurotunnel Le Shuttle, à vos côtés, porte à porte." },
    { q: "Quels documents faut-il pour mon animal entre la France et le Royaume-Uni ?", a: "Votre animal doit être identifié par puce électronique, vacciné contre la rage, et disposer d'un certificat sanitaire ou d'un passeport européen. Les chiens ont aussi besoin d'un traitement vermifuge administré par un vétérinaire 24 à 120 heures avant l'arrivée au Royaume-Uni." },
    { q: "D'où récupérez-vous vos clients à Paris ?", a: "De partout - aéroports Charles de Gaulle et Orly, tous les arrondissements de Paris, Disneyland Paris et la banlieue. Votre chauffeur vous attend aux arrivées avec une pancarte pour les prises en charge à l'aéroport." },
  ],
};

export const MARQUEE_ITEMS = {
  en: ["Paris Charles de Gaulle", "Orly", "Central Paris", "London St Pancras", "Folkestone Eurotunnel", "Calais Coquelles", "Brussels Midi", "Amsterdam Centraal", "Heathrow", "Gatwick"],
  fr: ["Paris Charles de Gaulle", "Orly", "Paris centre", "Londres St Pancras", "Folkestone Eurotunnel", "Calais Coquelles", "Bruxelles Midi", "Amsterdam Centraal", "Heathrow", "Gatwick"],
};

export const CHECKLIST = {
  en: [
    "ISO microchip, implanted before or on the day of the rabies vaccination",
    "Rabies vaccination at least 21 days old at travel",
    "Animal Health Certificate (within 10 days) or valid EU pet passport",
    "Dogs entering the UK: tapeworm treatment recorded 24-120 hours before arrival",
  ],
  fr: [
    "Puce électronique ISO, posée avant ou le jour du vaccin antirabique",
    "Vaccin antirabique d'au moins 21 jours au moment du voyage",
    "Certificat sanitaire (moins de 10 jours) ou passeport européen valide",
    "Chiens entrant au Royaume-Uni : vermifuge enregistré 24 à 120 h avant l'arrivée",
  ],
};

export const ROUTE_QUERIES = {
  en: [
    "pet taxi london to paris",
    "pet couriers uk",
    "door to door pet transport",
    "pet taxi calais to folkestone price",
    "pet check in calais ferry",
    "le shuttle pet reception",
    "pet taxi euston st pancras",
    "pet passport france to uk",
    "dog passport uk to europe",
    "uk pet entry requirements",
    "are dogs allowed on the eurostar",
    "how much does an ahc cost",
    "travelling with a dog from france to uk",
    "taking my dog to france from uk",
    "pet taxi london to brussels",
    "how much to take a dog to france",
    "pet taxi london to europe",
    "eurotunnel with dog",
    "eurotunnel with pets",
    "eurotunnel travelling with pets",
    "channel tunnel with dogs",
    "le shuttle pets",
    "folkestone pet taxi",
    "folkestone pet reception",
    "eurotunnel pet reception",
    "pet reception folkestone",
    "returning to uk from france with dog",
    "defra approved pet transport",
    "approved routes into uk for pets",
    "cheapest pet transport france to uk cost",
    "can you take dogs on eurostar",
    "pet friendly hotel calais",
    "what are the new rules for taking a dog to france",
    "how much does it cost to import a dog to uk",
    "eurotunnel pets to uk",
    "eurotunnel pet check-in folkestone",
    "cost of dog passport uk",
    "le shuttle pet passport",
    "calais eurotunnel pet reception",
    "pet taxi eurotunnel",
    "can you take dogs on eurotunnel",
    "pet taxi uk",
    "pet taxi paris to london price",
    "pet taxi france to uk",
    "pet transport uk to france",
    "pet taxi uk to france",
    "pet taxi paris london",
    "pet taxi paris to london",
    "pet reception calais",
    "pet taxi london to paris",
    "le shuttle calais pet reception",
    "calais pet reception",
    "le shuttle pet travel",
    "eurotunnel pet travel",
    "luxury pet transport",
    "pet chauffeur",
    "pet voyage uk reviews",
    "uk pet passport 2026",
    "pet transport scotland",
  ],
  fr: [
    "taxi animalier paris londres",
    "transport chien france angleterre",
    "transport animaux porte à porte",
    "prix taxi animalier calais folkestone",
    "enregistrement animaux ferry calais",
    "réception animaux le shuttle",
    "passeport animal france royaume-uni",
    "documents chien pour angleterre",
    "chiens autorisés eurostar",
    "combien coûte un certificat sanitaire animal",
    "voyager avec un chien de france au royaume-uni",
    "taxi animalier vers la france",
    "eurotunnel animaux vers le royaume-uni",
    "enregistrement animaux eurotunnel folkestone",
    "taxi animalier eurotunnel",
    "chiens acceptés eurotunnel",
    "taxi animalier paris londres prix",
    "transport animalier royaume-uni france",
    "taxi animalier londres paris",
    "réception animaux calais",
    "transport animalier de luxe",
    "chauffeur pour animaux",
    "passeport animal royaume-uni 2026",
  ],
};

export const BLOG_QUERIES = {
  en: [
    "pet transport abroad",
    "pets move abroad",
    "best pet relocation services uk",
    "cat courier service",
    "pet transport from spain to uk",
    "cat transport spain to uk",
    "dog transport uk to spain",
    "can you take your dog to jersey",
    "travelling to jersey with a dog",
    "taking a dog to switzerland from uk",
    "uk to italy pet transport",
    "what is pet taxi",
    "elite pet taxi uk",
    "pet delivery",
    "animal transport insurance",
    "how much for dog passport uk",
    "how much is ahc for dog",
    "euro tunnel with a dog",
    "ferry or eurotunnel with pets",
    "pet couriers uk",
    "how much is a dog passport",
    "how much is a dog passport uk",
    "eurotunnel pets",
    "cost of taking a dog to france",
    "can dogs travel in car for 12 hours",
    "how much does it cost to take a dog to france",
    "pet passport uk",
    "pet taxi",
    "pet transport",
    "pet transport scotland",
    "pet transport alicante to uk",
    "taking a dog to jersey from uk",
    "uk pet passport 2026",
    "pet voyage uk reviews",
    "luxury pet transport",
    "pet chauffeur",
  ],
  fr: [
    "transport d'animaux à l'étranger",
    "déménagement animaux à l'étranger",
    "meilleur service de relocation animaux",
    "transport chat espagne angleterre",
    "transport chien angleterre espagne",
    "emmener son chien à jersey",
    "emmener son chien en suisse",
    "c'est quoi un taxi animalier",
    "assurance transport animal",
    "prix passeport chien",
    "combien coûte un certificat sanitaire chien",
    "eurotunnel avec un chien",
    "ferry ou eurotunnel avec animaux",
    "prix passeport chien",
    "eurotunnel animaux",
    "coût pour emmener un chien en france",
    "combien coûte un passeport pour chien",
    "taxi animalier",
    "transport d'animaux écosse",
    "emmener son chien à jersey depuis le royaume-uni",
  ],
};